import React from 'react';
import { Alert, Button, Typography } from 'antd';
import { HistoryOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Visible scan reuse banner (review step only).
 *
 * The extraction cache already silently reuses a previous scan on a cache
 * hit (zero LLM tokens spent) — this banner is how the engineer SEES that
 * happened, with an explicit way to force a fresh, AI-token-spending
 * re-scan instead. Renders nothing when `cachedAt` is falsy — a fresh
 * (non-cached) extraction never shows this.
 *
 * A re-scan re-runs the full 6-pass extraction (measured ~4 minutes on a
 * 57-page handover, real AI spend), so that warning sits directly next to
 * the button rather than buried in a tooltip — the engineer needs to see
 * it before clicking, not after.
 *
 * @param {Object} props
 * @param {?string} props.cachedAt - ISO timestamp of the reused scan's
 *   original extraction (OnboardExtractResponse.cached_at). Falsy => null.
 * @param {number} [props.recordCount=0] - prerequisites.length + dependents.length
 *   from the reused proposal.
 * @param {() => void} props.onRescan - fired when "Re-scan with AI" is clicked.
 * @param {boolean} [props.rescanning=false] - true while a forced re-scan is
 *   in flight; puts the button into its loading + disabled state so a
 *   second click can't fire a concurrent re-scan.
 */
export default function ReuseBanner({ cachedAt, recordCount = 0, onRescan, rescanning = false }) {
  if (!cachedAt) return null;

  const reuseDate = new Date(cachedAt).toLocaleDateString();

  return (
    <Alert
      type="info"
      showIcon
      icon={<HistoryOutlined />}
      style={{ marginBottom: 16 }}
      message={`Reused a previous scan from ${reuseDate}`}
      description={
        <div>
          <Text>
            {recordCount} record{recordCount === 1 ? '' : 's'} loaded from the scan run on {reuseDate} —
            no AI tokens were spent.
          </Text>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              loading={rescanning}
              disabled={rescanning}
              onClick={onRescan}
            >
              Re-scan with AI
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Takes a few minutes and spends AI tokens — only re-scan if the source document changed.
            </Text>
          </div>
        </div>
      }
    />
  );
}
